/**
 * The soft warning for a line item whose size and weight can't both be right.
 *
 * Every hard limit is about one quantity at a time — this axis is too long, this
 * shipment is too heavy — so a line item can clear all of them and still be
 * impossible. Twelve hot tubs entered as 8x8x36in at 700lb each passed every one
 * of them, and 525 lb/ft3 is denser than solid steel. The wrong numbers then set
 * the freight class and the price.
 *
 * It never blocks: the customer can ignore it and book. Real freight does
 * occasionally run dense, and a rejection would be worse than a nudge.
 *
 * The reason travels in a question bubble rather than the field's own warning
 * mark, matching the other line-item messages — a bubble beside the words is
 * where customers already look on this form.
 */

import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import { LineItemsHighDensityQuestionBubble } from "../Bubbles/LineItemsHighDensityQuestionBubble";
import { lineItemsDensity } from "../Helpers/lineItemsDensity";

export function lineItemsDensityWarning(
  lineItem: AddLineItemType,
  limits: ShipmentLimits,
): ErrorMessageType {
  const { length, width, height, weightPerHandlingUnit } = lineItem;
  // A part-entered line item tells us nothing yet, so we stay quiet until all
  // four are in rather than warning at someone mid-keystroke.
  if (
    length === undefined ||
    width === undefined ||
    height === undefined ||
    weightPerHandlingUnit === undefined
  ) {
    return undefined;
  }

  const density = lineItemsDensity(
    weightPerHandlingUnit,
    length,
    width,
    height,
  );
  if (
    density === undefined ||
    density <= limits.advisory.maxDensityPoundsPerCubicFoot
  ) {
    return undefined;
  }

  return (
    <>
      High density? <LineItemsHighDensityQuestionBubble />
    </>
  );
}
